import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';

export default function HospitalSelector({
  hospitals, selectedId, onSelect, onClear, errors, translations
}) {
  var t = translations;

  var openState = useState(false);
  var isOpen = openState[0];
  var setIsOpen = openState[1];

  var searchState = useState('');
  var search = searchState[0];
  var setSearch = searchState[1];

  var containerRef = useRef(null);
  var searchInputRef = useRef(null);

  var items = hospitals || [];

  var selectedHospital = useMemo(function() {
    if (!selectedId) return null;
    return items.find(function(h) {
      return String(h.id || h['id']) === String(selectedId);
    }) || null;
  }, [selectedId, items]);

  var filtered = useMemo(function() {
    if (!search) return items;
    var q = search.toLowerCase();
    return items.filter(function(h) {
      return (h.name || h['name'] || '').toLowerCase().indexOf(q) !== -1;
    });
  }, [items, search]);

  var handleToggle = useCallback(function() {
    setIsOpen(function(prev) { return !prev; });
    setSearch('');
  }, []);

  var handleSelect = useCallback(function(hospital) {
    onSelect(hospital.id || hospital['id']);
    setIsOpen(false);
    setSearch('');
  }, [onSelect]);

  var handleClear = useCallback(function(e) {
    e.stopPropagation();
    onClear();
    setIsOpen(false);
    setSearch('');
  }, [onClear]);

  useEffect(function() {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(function() {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return function() { document.removeEventListener('mousedown', handleClickOutside); };
  }, []);

  var hasError = errors && errors.length > 0;
  var selectedName = selectedHospital ? (selectedHospital.name || selectedHospital['name']) : null;

  return (
    <div className="hospital-search-select" ref={containerRef}>
      <div
        className={'hospital-search-select__trigger' + (hasError ? ' hospital-search-select__trigger--error' : '')}
        onClick={handleToggle}
      >
        <span className={selectedName ? 'hospital-search-select__value' : 'hospital-search-select__placeholder'}>
          {selectedName || t.select_hospital}
        </span>
        <div className="hospital-search-select__icons">
          {selectedName && (
            <button type="button" className="hospital-search-select__clear" onClick={handleClear}>
              <i className="zmdi zmdi-close" />
            </button>
          )}
          <i className={'zmdi ' + (isOpen ? 'zmdi-chevron-up' : 'zmdi-chevron-down')} />
        </div>
      </div>

      {hasError && (
        <div className="text-danger small mt-1">
          <i className="zmdi zmdi-alert-circle-o me-1" />
          {errors.join(', ')}
        </div>
      )}

      {isOpen && (
        <div className="hospital-search-select__dropdown">
          <div className="hospital-search-select__search">
            <i className="zmdi zmdi-search" />
            <input
              ref={searchInputRef}
              type="text"
              className="hospital-search-select__search-input"
              placeholder={t.search_hospital}
              value={search}
              onChange={function(e) { setSearch(e.target.value); }}
              autoComplete="off"
            />
          </div>
          <div className="hospital-search-select__list">
            {filtered.length > 0 ? (
              filtered.map(function(h) {
                var hId = h.id || h['id'];
                var hName = h.name || h['name'];
                var isSelected = String(hId) === String(selectedId);
                return (
                  <div
                    key={hId}
                    className={'hospital-search-select__item' + (isSelected ? ' hospital-search-select__item--selected' : '')}
                    onClick={function() { handleSelect(h); }}
                  >
                    {hName}
                    {isSelected && <i className="zmdi zmdi-check" />}
                  </div>
                );
              })
            ) : (
              <div className="hospital-search-select__empty">{t.no_hospitals_found}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
